import React, { useState } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Paper,
  InputAdornment,
  CircularProgress,
  Link,
} from "@mui/material";
import axios from "axios";
import { Email as EmailIcon } from "@mui/icons-material";
import { backendURL } from "../url";

const PasswordReset: React.FC = () => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError(true);
      return;
    }
    
    try {
      setLoading(true);
      setError(false);
      setMessage("");

      const response = await axios.post(
        // `${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`,
        `${backendURL}/api/auth/forgot-password`,
        {
          email: email,
        }
      );
      
      if (response.status === 200) {
        setMessage("A password reset link has been sent to your email address. Please check your inbox.");
      }
    } catch (error) {
      console.error("Error resetting password:", error);
      setError(true);
      setMessage("We couldn't process your request. Please verify your email and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          borderRadius: 2,
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)"
        }}
      >
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Box 
            sx={{ 
              backgroundColor: "primary.light", 
              width: 70, 
              height: 70, 
              borderRadius: "50%", 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center",
              margin: "0 auto 16px"
            }}
          >
            <EmailIcon sx={{ fontSize: 36, color: "white" }} />
          </Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Reset Password
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Enter your email address below and we'll send you instructions to reset your password.
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email Address"
            variant="outlined"
            margin="normal"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError(false);
            }}
            error={error}
            helperText={error ? "Please enter a valid email address" : ""}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
            autoFocus
          />
          
          <Button
            variant="contained"
            color="primary"
            fullWidth
            type="submit"
            size="large"
            disabled={loading}
            sx={{ 
              py: 1.5, 
              fontWeight: "bold",
              textTransform: "none",
              fontSize: "1rem"
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : "Send Reset Link"}
          </Button>
        </form>

        {message && (
          <Alert 
            severity="success" 
            sx={{ 
              mt: 3, 
              borderRadius: 1,
              display: "flex",
              alignItems: "center"
            }}
          >
            {message}
          </Alert>
        )}

        <Box sx={{ mt: 3, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            Remember your password? {' '}
            <Link href="/login" underline="hover" fontWeight="medium">
              Back to Login
            </Link>
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default PasswordReset;